import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ArrowUpRight, User } from "lucide-react";
import { GhlSyncButton } from "@/components/GhlIntegration";

function agingTone(bucket, balance) {
  if (balance >= 0) return "border-emerald-500/40 text-emerald-300 bg-emerald-500/10";
  if (bucket === "90_plus") return "border-rose-500/40 text-rose-300 bg-rose-500/10";
  if (bucket === "60_90") return "border-orange-500/40 text-orange-300 bg-orange-500/10";
  if (bucket === "30_60") return "border-amber-500/40 text-amber-300 bg-amber-500/10";
  return "border-white/10 text-zinc-300";
}

export default function ResidentsPage() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ property_id: propertyId });
      if (q) params.set("q", q);
      if (status !== "all") params.set("status", status);
      const { data } = await api.get(`/admin/residents?${params}`);
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, [propertyId, q, status]);

  // Immediate first load; subsequent filter changes get a 200ms debounce.
  useEffect(() => { load(); }, [propertyId, load]);
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [q, status, load]);

  const kpis = useMemo(() => {
    const delinquent = items.filter((r) => r.balance < 0).length;
    const totalOwed = Math.abs(items.reduce((s, r) => s + Math.min(0, r.balance || 0), 0));
    return { total: items.length, delinquent, totalOwed };
  }, [items]);

  return (
    <div>
      <PageHeader
        eyebrow="Directory"
        title="Residents"
        subtitle="Every resident on this property — searchable, sortable, one click into a full 360 view."
        actions={
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-white/10">{kpis.total} residents</Badge>
            {kpis.delinquent > 0 && (
              <Badge variant="outline" className="border-rose-500/40 text-rose-300 bg-rose-500/10">
                {kpis.delinquent} delinquent · ${kpis.totalOwed.toFixed(2)}
              </Badge>
            )}
          </div>
        }
      />

      <Card className="border-white/10 bg-card p-4 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              data-testid="residents-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email, unit, or phone"
              className="pl-9 bg-white/[0.02] border-white/10"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="residents-status" className="w-44 bg-white/[0.02] border-white/10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All residents</SelectItem>
              <SelectItem value="current">Current only</SelectItem>
              <SelectItem value="delinquent">Delinquent only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="border-white/10 bg-card overflow-hidden">
        <div className="max-h-[68vh] overflow-y-auto scroll-slim">
          {items.map((r) => (
            <Link
              key={r.id}
              to={`/admin/residents/${r.id}`}
              data-testid={`resident-row-${r.id}`}
              className="flex items-center gap-4 px-4 py-3 border-b border-white/5 hover:bg-white/[0.03] transition"
            >
              <div className="h-10 w-10 rounded-md border border-white/10 bg-white/[0.03] flex items-center justify-center shrink-0">
                <User className="h-4 w-4 text-zinc-400" />
              </div>
              <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-5 gap-2 items-center">
                <div className="min-w-0">
                  <div className="font-medium truncate">{r.name}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{r.email}</div>
                </div>
                <div className="text-sm text-zinc-300">Unit {r.unit || "—"}</div>
                <div className="text-sm text-zinc-400 truncate">{r.phone || "No phone"}</div>
                <div className="flex items-center justify-start md:justify-center">
                  <GhlSyncButton uid={r.id} contactId={r.ghl_contact_id} compact />
                </div>
                <div className="flex items-center gap-2 justify-end">
                  <Badge variant="outline" className={agingTone(r.aging_bucket, r.balance)}>
                    ${r.balance?.toFixed(2)}
                  </Badge>
                  <ArrowUpRight className="h-4 w-4 text-zinc-500" />
                </div>
              </div>
            </Link>
          ))}
          {!loading && items.length === 0 && (
            <div className="px-6 py-10 text-center text-muted-foreground">
              No residents match. Try clearing the filters.
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
